import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Code,
  Group,
  List,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
  Alert,
  Menu,
} from "@mantine/core";
import {
  IconAdjustments,
  IconBook,
  IconCircleCheck,
  IconChevronDown,
  IconExclamationMark,
  IconListDetails,
  IconPlayerStop,
  IconRoute,
  IconSettings,
} from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useLocalStorage } from "@mantine/hooks";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Page, FormSectionCard } from "../../layout/page";
import PageListHeader from "../../components/pageListHeader";
import DataTable from "../../components/dataTable";
import { buildSearchQuery } from "../../components/pageList";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useAuthUser } from "react-auth-kit";
import { getFileBackupJobTableSchema } from "../../helpers/fileBackupJobTableSchema";
import {
  buildColumnFilterClause,
  buildDeselectAllClause,
  expandTableFilterQuery as expandColumnFilterQuery,
} from "../../helpers/tableFilterQuery";
import {
  DEFAULT_PATH_TEMPLATES,
  DEFAULT_ENTITY_TYPE_ENABLED,
  DEFAULT_WORKER,
  ENTITY_BACKUP_TYPE_META,
  normalizeFileBackupSettings,
  parseServiceAccountEmailFromJson,
  getFileBackupApiErrorMessage,
  getFileBackupErrorPresentation,
  serializeFileBackupSettings,
  validateFileBackupSettings,
} from "../../helpers/fileBackupSettingsUtils";

const FileBackupErrorHelpModal = ({ opened, onClose, presentation }) => {
  const { t } = useTranslation();
  if (!presentation) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(presentation.title)}
      size="lg"
      centered
    >
      <Stack spacing="md">
        <Alert color="red" variant="light" title={t("What happened")}>
          <Text size="sm">{t(presentation.summary)}</Text>
        </Alert>

        {(presentation.folderId || presentation.serviceAccountEmail) && (
          <Stack spacing="sm">
            {presentation.folderId && (
              <Stack spacing={4}>
                <Text size="xs" weight={600} color="dimmed">
                  {t("Root folder ID")}
                </Text>
                <Code block sx={{ wordBreak: "break-all" }}>
                  {presentation.folderId}
                </Code>
              </Stack>
            )}
            {presentation.serviceAccountEmail && (
              <Stack spacing={4}>
                <Text size="xs" weight={600} color="dimmed">
                  {t("Share the folder with this email (Editor access)")}
                </Text>
                <Code block sx={{ wordBreak: "break-all" }}>
                  {presentation.serviceAccountEmail}
                </Code>
              </Stack>
            )}
          </Stack>
        )}

        {presentation.steps?.length > 0 && (
          <Stack spacing="xs">
            <Text size="sm" weight={600}>{t("What to do")}</Text>
            <List spacing="xs" size="sm" withPadding>
              {presentation.steps.map((step) => (
                <List.Item key={step}>
                  <Text size="sm">{t(step)}</Text>
                </List.Item>
              ))}
            </List>
          </Stack>
        )}

        {presentation.technical && (
          <Text size="xs" color="dimmed">
            {t("Technical details")}: {presentation.technical}
          </Text>
        )}
      </Stack>
    </Modal>
  );
};

const SETUP_GUIDE_STEPS = [
  {
    title: "Create a Google Cloud service account",
    items: [
      "Sign in to Google Cloud Console (https://console.cloud.google.com).",
      "Create a new project, or select the project you want MBID to use for Drive access.",
      "Open APIs & Services → Library, search for Google Drive API, and click Enable.",
      "Go to APIs & Services → Credentials → Create credentials → Service account.",
      "Enter a service account name (for example MBID File Backup) and finish creating the account.",
      "Open the new service account → Keys → Add key → Create new key → JSON.",
      "Download the JSON key file and keep it secure. You will paste its contents into MBID in Step 4.",
    ],
  },
  {
    title: "Paste credentials in MBID",
    items: [
      "In MBID, go to Settings → Others → File Backup Sync.",
      "Open the Settings tab.",
      "Paste the full service account JSON into the Service account JSON field.",
      "Click Save. The page shows Credentials configured and the service account email from your JSON.",
      "Copy that email — you need it for the next step.",
    ],
  },
  {
    title: "Prepare the Google Drive folder",
    items: [
      "In Google Drive, create a root folder for MBID backups (or use a folder the client already shares with you).",
      "Example folder name: MBID File Backup or Client Name – MBID Uploads.",
      "Open the folder, click Share, and add the service account email shown on the MBID Settings tab.",
      "Grant Editor access so MBID can create folders and upload files.",
      "Copy the folder ID from the browser URL. It is the string after /folders/ in the address bar.",
      "Example URL: https://drive.google.com/drive/folders/1AbCdeFgHiJkLmNoPqRsTuVwXyZ → folder ID is 1AbCdeFgHiJkLmNoPqRsTuVwXyZ.",
    ],
  },
  {
    title: "Configure MBID — folder and test",
    items: [
      "On the File Backup Sync Settings tab, paste the Google Drive root folder ID.",
      "Click Save.",
      "Click Test connection. A success message confirms MBID can access the shared folder.",
    ],
  },
  {
    title: "Enable sync and tune options",
    items: [
      "Turn on Enable file backup sync.",
      "Confirm Provider is Google Drive.",
      "Choose whether Trash files on delete should move removed MBID files to Google Drive trash.",
      "Open the Path templates tab and turn on only the file types you need (e.g. Debit Note only).",
      "Optional: customize folder path templates for each enabled file type.",
      "Optional: open the Advanced settings tab to change Max retries and Batch size for the background worker.",
      "Click Save.",
    ],
  },
  {
    title: "Backfill existing files and verify",
    items: [
      "After saving with sync enabled, click Backfill to enqueue uploads for files already on the MBID server.",
      "Backfill may take time for large archives. The worker runs every few minutes in the background.",
      "Watch the status counters: Pending should decrease and Sync records should increase.",
      "Open the Sync Jobs tab to see individual pending, completed, or failed jobs and error messages.",
      "Upload a test file to a Debit Note and confirm it appears under the expected folder in Google Drive.",
    ],
  },
  {
    title: "Expected folder structure (Debit Note example)",
    items: [
      "Under your root folder, MBID creates paths like:",
      "Debit Note / 2026 / 202601 / DN26010001 / policy.pdf",
      "Year and month come from the debit note date or DN code (DN2601… → 2026 / 202601).",
      "Other entities use their own templates (Orders, Clients, Uploads fallback, etc.).",
    ],
  },
  {
    title: "Troubleshooting",
    items: [
      "Test connection fails: verify the folder is shared with the exact service account email shown on the page.",
      "Uploads stay pending: confirm Enable file backup sync is on and the processFileBackupJobs cron job is active.",
      "Open the Sync Jobs tab to see individual pending/failed uploads, delete, and rename jobs with error details.",
      "Synced files appear in subfolders under the root folder (e.g. Debit Note/year/yearMonth/code), not in the root itself.",
      "Wrong folder path: check Path templates on the Settings tab and ensure uploads pass entity context (e.g. debit note code).",
      "Removing a file from a debit note enqueues a delete job; check Sync Jobs tab to confirm it completed on Google Drive.",
      "Credentials lost after save: only paste new JSON when replacing credentials; leaving the field blank keeps the stored key.",
      "Sync is one-way only: changes made directly in Google Drive are not imported back into MBID.",
    ],
  },
];

const FileBackupActionMenuItem = ({
  title,
  description,
  onClick,
  disabled,
  color,
}) => (
  <Menu.Item onClick={onClick} disabled={disabled} color={color}>
    <Stack spacing={2}>
      <Text size="sm" weight={500} lineClamp={1}>{title}</Text>
      <Text size="xs" color="dimmed" lineClamp={2}>{description}</Text>
    </Stack>
  </Menu.Item>
);

const getTableColumns = (tableSchema) => tableSchema?.columns ?? [];

const FileBackupSyncJobsTab = ({
  api,
  onRefreshStatus,
  jobStatus,
  statusLoading = false,
  isActive = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const tabNav = useTabNavigate();
  const auth = useAuthUser();
  const userRole = auth()?.userRole;
  const apiRef = useRef(api);
  apiRef.current = api;
  const onRefreshStatusRef = useRef(onRefreshStatus);
  onRefreshStatusRef.current = onRefreshStatus;
  const tableSchema = useMemo(
    () =>
      getFileBackupJobTableSchema({
        tabNav,
        userRole,
        currentPathname: location.pathname,
      }),
    [tabNav.openDetail, userRole, location.pathname]
  );
  const filterStorageKey =
    "tableQuery" + location.pathname + "fileBackupJob" + (tableSchema.name ?? "");

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    totalPage: 1,
    page: 1,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState({});
  const [tableFilterQuery, setTableFilterQuery] = useLocalStorage({
    key: filterStorageKey,
    defaultValue: {},
  });
  const [sort, setSort] = useState({ by: "createdAt", order: "desc" });
  const [refreshing, setRefreshing] = useState(false);

  const buildQuery = useCallback(() => {
    const queries = [
      searchQuery,
      expandColumnFilterQuery(tableFilterQuery, tableSchema, () =>
        getTableColumns(tableSchema)
      ),
    ].filter((q) => !_.isEmpty(q));
    if (_.isEmpty(queries)) return {};
    return { $and: queries };
  }, [searchQuery, tableFilterQuery, tableSchema]);

  const fetchData = useCallback(async () => {
    if (!isActive) return;
    try {
      const data = await apiRef.current.search({
        apiEntity: "fileBackupJob",
        pageSize,
        currentPage,
        sort: sort.by ? sort : { by: "createdAt", order: "desc" },
        searchQuery: buildQuery(),
        searchText,
        searchByTextField: false,
      });
      if (!data) return;
      setPagination((prev) => ({
        ...prev,
        total: data.total,
        totalPage: Math.ceil(data.total / data.limit) || 1,
        page: currentPage,
      }));
      setRows(data.docs ?? []);
    } catch (error) {
      console.log(error);
      setRows([]);
      setPagination({ totalPage: 0, page: 1, total: 0 });
    }
  }, [
    isActive,
    pageSize,
    currentPage,
    sort,
    buildQuery,
    searchText,
  ]);

  useEffect(() => {
    if (!isActive) return;
    onRefreshStatusRef.current?.();
  }, [isActive]);

  useEffect(() => {
    const query =
      searchText === ""
        ? {}
        : buildSearchQuery(searchText, tableSchema.searchableFields);
    setSearchQuery(query);
  }, [searchText, tableSchema.searchableFields]);

  useEffect(() => {
    if (!isActive) return;
    fetchData();
  }, [isActive, searchQuery, pageSize, sort, currentPage, tableFilterQuery]);

  const onPageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      setPagination((prev) => ({ ...prev, page }));
    },
    []
  );

  const onPageSizeChange = useCallback((nextPageSize) => {
    setCurrentPage(1);
    setPageSize(nextPageSize);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const onSearchChange = useCallback(
    (text) => {
      if (currentPage > 1) {
        setCurrentPage(1);
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
      setSearchText(text);
    },
    [currentPage]
  );

  const onTableFilterChange = useCallback(
    (field, values, possibleValues) => {
      if (!field) return;
      const newFilterQuery = { ...tableFilterQuery };
      const columns = getTableColumns(tableSchema);
      const filterCol = columns.find((c) => c.filter?.field === field);

      if (values.length === 0) {
        newFilterQuery[field] = buildDeselectAllClause(filterCol);
        setCurrentPage(1);
        setTableFilterQuery(newFilterQuery);
        return;
      }

      if (values && possibleValues && values.length === possibleValues.length) {
        delete newFilterQuery[field];
        setCurrentPage(1);
        setTableFilterQuery(newFilterQuery);
        return;
      }

      newFilterQuery[field] = buildColumnFilterClause(filterCol, values);
      setCurrentPage(1);
      setTableFilterQuery(newFilterQuery);
    },
    [tableFilterQuery, tableSchema, setTableFilterQuery]
  );

  const onSortChange = useCallback((by, reverseSortDirection) => {
    if (!by) return;
    setSort({ by, order: reverseSortDirection ? "desc" : "asc" });
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchData();
      await onRefreshStatusRef.current?.();
    } finally {
      setRefreshing(false);
    }
  }, [fetchData]);

  if (!isActive) return null;

  const pendingCount = jobStatus?.pending ?? 0;
  const processingCount = jobStatus?.processing ?? 0;
  const syncedLast24Hours = jobStatus?.syncedLast24Hours ?? 0;

  return (
    <FormSectionCard
      title="Sync jobs"
      description="Background upload, delete, and rename jobs queued for Google Drive. Files appear in subfolders under the root folder, not in the root itself."
    >
      {jobStatus && (
        <Paper p="xs" mb="xs" withBorder mx="md">
          <Group spacing="lg">
            <Group spacing={4}>
              <Text size="xs" color="dimmed">{t("Pending jobs")}</Text>
              <Text size="xs" weight={600}>{pendingCount}</Text>
              {processingCount > 0 ? (
                <Text size="xs" color="dimmed">
                  ({t("processing")}: {processingCount})
                </Text>
              ) : null}
            </Group>
            <Group spacing={4}>
              <Text size="xs" color="dimmed">{t("Synced (last 24h)")}</Text>
              <Text size="xs" weight={600}>{syncedLast24Hours}</Text>
            </Group>
            {statusLoading ? (
              <Text size="xs" color="dimmed">{t("Refreshing…")}</Text>
            ) : null}
          </Group>
        </Paper>
      )}
      <DataTable
        data={rows}
        columns={tableSchema.columns}
        pagination={pagination}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortChange={onSortChange}
        onSearchChange={onSearchChange}
        onFilterChange={onTableFilterChange}
        tableFilterQuery={tableFilterQuery}
        searchText={searchText}
        searchBarExtra={
          <Button
            size="sm"
            variant="default"
            loading={refreshing}
            onClick={handleRefresh}
          >
            {t("Refresh")}
          </Button>
        }
        readonly
        padding="md"
        columnFilterLayout="toolbar"
      />
      {rows.length === 0 && pagination.total === 0 && (
        <Text size="sm" color="dimmed" align="center" py="md">
          {t("No sync jobs yet.")}
        </Text>
      )}
    </FormSectionCard>
  );
};

const FileBackupSetupGuideTab = ({ serviceAccountEmail }) => {
  const { t } = useTranslation();

  return (
    <Stack spacing="lg">
      <FormSectionCard>
        <Stack spacing="sm">
          <Title order={5}>{t("Overview")}</Title>
          <Text size="sm" color="dimmed">
            {t(
              "File Backup Sync mirrors internal staff file attachments to Google Drive one-way. MBID remains the source of truth. Consultant-only file lists are not included."
            )}
          </Text>
          {serviceAccountEmail && (
            <Text size="sm">
              {t("Current service account")}:{" "}
              <Code>{serviceAccountEmail}</Code>
            </Text>
          )}
        </Stack>
      </FormSectionCard>

      {SETUP_GUIDE_STEPS.map((section, sectionIndex) => (
        <FormSectionCard key={section.title}>
          <Group spacing="sm" mb="sm" noWrap align="flex-start">
            <ThemeIcon size="lg" radius="xl" variant="light">
              {sectionIndex + 1}
            </ThemeIcon>
            <Title order={5}>{t(section.title)}</Title>
          </Group>
          <List spacing="xs" size="sm" withPadding>
            {section.items.map((item) => (
              <List.Item key={item}>
                <Text size="sm" color="dimmed">{t(item)}</Text>
              </List.Item>
            ))}
          </List>
        </FormSectionCard>
      ))}
    </Stack>
  );
};

const FileBackupSyncSetting = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [activeTab, setActiveTab] = useState("settings");
  const [settingId, setSettingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [jobStatus, setJobStatus] = useState(null);
  const [backfillRun, setBackfillRun] = useState(null);
  const [connectionHelp, setConnectionHelp] = useState(null);
  const [connectionHelpOpen, setConnectionHelpOpen] = useState(false);

  const [formValues, setFormValues] = useState({
    enabled: false,
    provider: "googleDrive",
    googleDrive: {
      rootFolderId: "",
      trashOnDelete: true,
    },
    serviceAccountJson: "",
    pathTemplates: { ...DEFAULT_PATH_TEMPLATES },
    entityTypeEnabled: { ...DEFAULT_ENTITY_TYPE_ENABLED },
    worker: { ...DEFAULT_WORKER },
    credentialsConfigured: false,
    serviceAccountEmail: "",
  });

  const displayedServiceAccountEmail =
    formValues.serviceAccountEmail ||
    parseServiceAccountEmailFromJson(formValues.serviceAccountJson);

  const showFileBackupFailure = (result, { useHelpModal = false } = {}) => {
    const presentation = getFileBackupErrorPresentation(result, api.getErrorMsg);
    if (!presentation) {
      showNotification({
        title: t("Error"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("Action failed."),
      });
      return;
    }

    if (useHelpModal) {
      setConnectionHelp(presentation);
      setConnectionHelpOpen(true);
      return;
    }

    showNotification({
      title: t(presentation.title),
      color: "red",
      icon: <IconExclamationMark size={18} />,
      message: t(presentation.summary),
      autoClose: 12000,
    });
  };

  const refreshStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const result = await api.fileBackup.getStatus();
      if (result?.success) {
        setJobStatus(result.data?.status || null);
        const latestBackfill = result.data?.status?.backfillRun;
        if (latestBackfill?.status === "running") {
          setBackfillRun(latestBackfill);
        }
        if (result.data?.settings) {
          setFormValues((prev) => ({
            ...normalizeFileBackupSettings(result.data.settings),
            serviceAccountJson: "",
          }));
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setStatusLoading(false);
    }
  }, [api]);

  const refreshStatusRef = useRef(refreshStatus);
  refreshStatusRef.current = refreshStatus;

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });

      if (!result || _.isEmpty(result.docs)) {
        setSettingId(null);
        await refreshStatus();
        return;
      }

      const setting = result.docs[0];
      setSettingId(setting._id);
      setFormValues({
        ...normalizeFileBackupSettings(setting.fileBackup),
        serviceAccountJson: "",
      });
      await refreshStatus();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  useEffect(() => {
    if (backfillRun?.status !== "running" || !backfillRun?._id) return undefined;

    const runId = backfillRun._id;

    const pollBackfill = async () => {
      const result = await api.fileBackup.backfillStatus(runId);
      if (result?.success) {
        if (!result.data) {
          setBackfillRun(null);
          return;
        }
        setBackfillRun(result.data);
        if (result.data.status !== "running") {
          await refreshStatusRef.current();
        }
      }
    };

    pollBackfill();
    const timer = setInterval(pollBackfill, 5000);
    return () => clearInterval(timer);
  }, [backfillRun?._id, backfillRun?.status, api]);

  const estimatedJobsPerHour = useMemo(() => {
    const batchSize = formValues.worker?.batchSize ?? DEFAULT_WORKER.batchSize;
    return batchSize * 30;
  }, [formValues.worker?.batchSize]);

  const updateField = (path, value) => {
    setFormValues((prev) => {
      const next = _.cloneDeep(prev);
      _.set(next, path, value);
      return next;
    });
  };

  const handleSave = async () => {
    const validationError = validateFileBackupSettings(formValues);
    if (validationError) {
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: validationError,
      });
      return;
    }

    try {
      setSaving(true);
      const fileBackupPayload = serializeFileBackupSettings({
        formValues,
        credentialsConfigured: formValues.credentialsConfigured,
        serviceAccountEmail: formValues.serviceAccountEmail,
      });
      delete fileBackupPayload.credentialsConfigured;
      delete fileBackupPayload.serviceAccountEmail;

      const result = await api.fileBackup.saveSettings(fileBackupPayload);

      if (result?.success) {
        showNotification({
          title: t("Save"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message: t("File backup settings saved."),
        });
        if (result.data) {
          setFormValues({
            ...normalizeFileBackupSettings(result.data),
            serviceAccountJson: "",
          });
          if (!settingId) {
            const settingResult = await api.search({
              apiEntity: "setting",
              pageSize: 1,
            });
            if (settingResult?.docs?.[0]?._id) {
              setSettingId(settingResult.docs[0]._id);
            }
          }
        }
        await refreshStatus();
        return;
      }

      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: getFileBackupApiErrorMessage(
          result,
          api.getErrorMsg,
          t("Failed to save settings.")
        ),
      });
      if (result?.error?.fileBackupError) {
        const presentation = getFileBackupErrorPresentation(result, api.getErrorMsg);
        setConnectionHelp(presentation);
        setConnectionHelpOpen(true);
      }
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("Failed to save settings."),
      });
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (actionName, fn) => {
    try {
      setActionLoading(actionName);
      const result = await fn();
      if (result?.success) {
        const folderName = result.data?.folderName;
        if (actionName === "test") {
          setConnectionHelp(null);
          setConnectionHelpOpen(false);
        }
        showNotification({
          title: t("Success"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message:
            actionName === "stopBackfill" && result.data?.removedPendingJobs != null
              ? t("Backfill stopped. Removed {{count}} pending upload jobs.", {
                  count: result.data.removedPendingJobs,
                })
              : actionName === "clearPending" && result.data?.removed != null
                ? t("Removed {{count}} pending sync jobs.", {
                    count: result.data.removed,
                  })
                : actionName === "reconcileDrive" && result.data?.requeued != null
                  ? t(
                      "Re-queued {{requeued}} missing Drive uploads (checked {{checked}}).",
                      {
                        requeued: result.data.requeued,
                        checked: result.data.checked,
                      }
                    )
                  : actionName === "processJobsNow" && result.data?.processed != null
                    ? t("Processed {{count}} backup jobs this run.", {
                        count: result.data.processed,
                      })
                    : folderName
                ? t("Connected to folder: {{name}}", { name: folderName })
                : result.message || t("Completed successfully."),
        });
        if (actionName === "stopBackfill") {
          setBackfillRun(null);
        }
        await refreshStatus();
      } else {
        showFileBackupFailure(result, {
          useHelpModal: actionName === "test",
        });
      }
    } catch (error) {
      showNotification({
        title: t("Error"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: error?.message || t("Action failed."),
      });
    } finally {
      setActionLoading("");
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("File Backup Sync")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled:
              loading ||
              activeTab === "setupGuide",
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />

      {!loading && (
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List mb="md">
            <Tabs.Tab value="settings" icon={<IconSettings size={14} />}>
              {t("Settings")}
            </Tabs.Tab>
            <Tabs.Tab value="syncJobs" icon={<IconListDetails size={14} />}>
              {t("Sync Jobs")}
            </Tabs.Tab>
            <Tabs.Tab value="pathTemplates" icon={<IconRoute size={14} />}>
              {t("Path templates")}
            </Tabs.Tab>
            <Tabs.Tab value="advanced" icon={<IconAdjustments size={14} />}>
              {t("Advanced settings")}
            </Tabs.Tab>
            <Tabs.Tab value="setupGuide" icon={<IconBook size={14} />}>
              {t("Setup guide")}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="settings">
            <Stack spacing="md">
              <FormSectionCard
                title="Basic settings"
                description="Enable sync, choose the provider, and set the Google Drive destination folder."
              >
                <Stack spacing="md">
                  <Stack spacing="sm">
                    <Switch
                      size="md"
                      label={t("Enable file backup sync")}
                      checked={formValues.enabled}
                      onChange={(event) =>
                        updateField("enabled", event.currentTarget.checked)
                      }
                    />
                    <Switch
                      size="md"
                      label={t("Move to Google Drive trash on delete")}
                      checked={formValues.googleDrive?.trashOnDelete !== false}
                      onChange={(event) =>
                        updateField("googleDrive.trashOnDelete", event.currentTarget.checked)
                      }
                    />
                  </Stack>
                  <Select
                    label={t("Provider")}
                    value={formValues.provider}
                    onChange={(value) => updateField("provider", value || "googleDrive")}
                    data={[
                      { value: "googleDrive", label: "Google Drive" },
                      { value: "s3", label: "AWS S3 (coming soon)", disabled: true },
                      { value: "vps", label: "VPS (coming soon)", disabled: true },
                    ]}
                  />
                  <TextInput
                    label={t("Root folder ID")}
                    description={
                      displayedServiceAccountEmail
                        ? t(
                            "Share this folder with the service account email below, then paste the folder ID from the Drive URL."
                          )
                        : t(
                            "Paste and save credentials below first, then share your folder and paste its ID here."
                          )
                    }
                    placeholder="1AbCdeFgHiJkLmNoPqRsTuVwXyZ"
                    value={formValues.googleDrive?.rootFolderId || ""}
                    onChange={(event) =>
                      updateField("googleDrive.rootFolderId", event.currentTarget.value)
                    }
                  />
                </Stack>
              </FormSectionCard>

              <FormSectionCard
                title="Google service account"
                description="Paste the JSON key once; it is stored securely and not shown again."
              >
                <Stack spacing="sm">
                  <Group spacing="xs">
                    {formValues.credentialsConfigured ? (
                      <Badge color="green" variant="light">
                        {t("Credentials configured")}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        {t("Credentials not configured")}
                      </Badge>
                    )}
                    {displayedServiceAccountEmail && (
                      <Text size="sm" color="dimmed">
                        {t("Service account email")}:{" "}
                        <Text span weight={500} color="gray.3">
                          {displayedServiceAccountEmail}
                        </Text>
                      </Text>
                    )}
                  </Group>
                  <Textarea
                    label={t("Service account JSON")}
                    description={
                      formValues.credentialsConfigured
                        ? t(
                            "Credentials are saved on the server. The field stays blank for security — paste new JSON only to replace them."
                          )
                        : t(
                            "Paste the full JSON key file contents from Google Cloud."
                          )
                    }
                    placeholder={
                      formValues.credentialsConfigured
                        ? t("Credentials saved — paste here only to replace…")
                        : t("Paste JSON here…")
                    }
                    minRows={5}
                    value={formValues.serviceAccountJson}
                    onChange={(event) =>
                      updateField("serviceAccountJson", event.currentTarget.value)
                    }
                  />
                </Stack>
              </FormSectionCard>

              <FormSectionCard
                title="Actions & sync status"
                description="Test setup before enabling sync. Backfill copies existing server files; retry re-queues failed jobs."
              >
                <Stack spacing="md">
                  <Group>
                    <Button
                      variant="filled"
                      loading={actionLoading === "test"}
                      onClick={() =>
                        runAction("test", () => api.fileBackup.testConnection())
                      }
                    >
                      {t("Test connection")}
                    </Button>
                    <Menu shadow="md" width={340} position="bottom-start">
                      <Menu.Target>
                        <Button
                          variant="light"
                          rightIcon={<IconChevronDown size={16} />}
                          loading={
                            Boolean(actionLoading) && actionLoading !== "test"
                          }
                        >
                          {t("Action")}
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <FileBackupActionMenuItem
                          title={t("Backfill existing files")}
                          description={t(
                            "Scan MBID and queue upload jobs for unsynced files."
                          )}
                          disabled={
                            Boolean(actionLoading) ||
                            backfillRun?.status === "running"
                          }
                          onClick={() => {
                            if (
                              !window.confirm(
                                t(
                                  "Queue upload jobs for all unsynced files? This runs in the background (~{{rate}} jobs/hour at current batch size). MBID local files are read-only and never deleted or moved by sync.",
                                  { rate: estimatedJobsPerHour }
                                )
                              )
                            ) {
                              return;
                            }
                            runAction("backfill", async () => {
                              const result = await api.fileBackup.backfill();
                              if (result?.success && result.data?.backfillRunId) {
                                const statusResult = await api.fileBackup.backfillStatus(
                                  result.data.backfillRunId
                                );
                                if (statusResult?.success) {
                                  setBackfillRun(statusResult.data);
                                }
                              }
                              return result;
                            });
                          }}
                        />
                        <FileBackupActionMenuItem
                          title={t("Process jobs now")}
                          description={t(
                            "Run one worker batch now (about {{rate}} jobs/hour at current settings).",
                            { rate: estimatedJobsPerHour }
                          )}
                          disabled={Boolean(actionLoading)}
                          onClick={() =>
                            runAction("processJobsNow", () =>
                              api.fileBackup.processJobsNow()
                            )
                          }
                        />
                        <FileBackupActionMenuItem
                          title={t("Retry failed jobs")}
                          description={t("Re-queue all failed jobs as pending.")}
                          disabled={Boolean(actionLoading)}
                          onClick={() =>
                            runAction("retry", () => api.fileBackup.retryFailed())
                          }
                        />
                        <Menu.Divider />
                        <FileBackupActionMenuItem
                          title={t("Clear pending jobs")}
                          description={t(
                            "Remove all pending queue jobs. Cannot be undone."
                          )}
                          color="red"
                          disabled={Boolean(actionLoading) || !jobStatus?.pending}
                          onClick={() => {
                            if (
                              !window.confirm(
                                t(
                                  "Remove all {{count}} pending sync jobs? This cannot be undone. Processing and completed jobs are not affected.",
                                  { count: jobStatus?.pending ?? 0 }
                                )
                              )
                            ) {
                              return;
                            }
                            runAction("clearPending", async () => {
                              const result = await api.fileBackup.clearPending();
                              if (result?.success) {
                                setBackfillRun(null);
                              }
                              return result;
                            });
                          }}
                        />
                        <FileBackupActionMenuItem
                          title={t("Re-upload missing Drive files")}
                          description={t(
                            "Compare Drive with sync records; re-queue missing uploads (100 per run)."
                          )}
                          disabled={Boolean(actionLoading)}
                          onClick={() => {
                            if (
                              !window.confirm(
                                t(
                                  "Check synced files against Google Drive and re-queue uploads that are missing? This clears stale folder cache and runs up to 100 files per click."
                                )
                              )
                            ) {
                              return;
                            }
                            runAction("reconcileDrive", () =>
                              api.fileBackup.reconcileDrive(100)
                            );
                          }}
                        />
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  {backfillRun && (
                    <Alert
                      color={
                        backfillRun.status === "failed"
                          ? "red"
                          : backfillRun.status === "completed"
                            ? "green"
                            : "blue"
                      }
                      variant="light"
                    >
                      <Stack spacing={4}>
                        <Group position="apart" align="flex-start" noWrap>
                          <Text size="sm" weight={600}>
                            {backfillRun.status === "running"
                              ? t("Backfill in progress")
                              : backfillRun.status === "completed"
                                ? t("Backfill completed")
                                : t("Backfill failed")}
                          </Text>
                          {backfillRun.status === "running" ? (
                            <Button
                              size="xs"
                              color="red"
                              variant="light"
                              leftIcon={<IconPlayerStop size={14} />}
                              loading={actionLoading === "stopBackfill"}
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    t(
                                      "Stop this backfill and remove pending upload jobs queued by it? Jobs already processing will continue."
                                    )
                                  )
                                ) {
                                  return;
                                }
                                runAction("stopBackfill", () =>
                                  api.fileBackup.stopBackfill(backfillRun._id)
                                );
                              }}
                            >
                              {t("Stop backfill")}
                            </Button>
                          ) : null}
                        </Group>
                        <Text size="xs" color="dimmed">
                          {t("Docs scanned")}: {backfillRun.scannedDocs ?? 0} ·{" "}
                          {t("Files scanned")}: {backfillRun.scannedFiles ?? 0} ·{" "}
                          {t("Jobs queued")}: {backfillRun.enqueued ?? 0} ·{" "}
                          {t("Already synced")}: {backfillRun.skippedSynced ?? 0} ·{" "}
                          {t("Duplicates skipped")}: {backfillRun.skippedDuplicate ?? 0}
                        </Text>
                        {backfillRun.lastError ? (
                          <Text size="xs" color="red">
                            {backfillRun.lastError}
                          </Text>
                        ) : null}
                      </Stack>
                    </Alert>
                  )}
                  {connectionHelp && (
                    <Alert
                      color="red"
                      variant="light"
                      title={t(connectionHelp.title)}
                      withCloseButton
                      onClose={() => {
                        setConnectionHelp(null);
                        setConnectionHelpOpen(false);
                      }}
                    >
                      <Stack spacing="xs">
                        <Text size="sm">{t(connectionHelp.summary)}</Text>
                        <Button
                          variant="light"
                          color="red"
                          size="xs"
                          onClick={() => setConnectionHelpOpen(true)}
                        >
                          {t("View step-by-step fix")}
                        </Button>
                      </Stack>
                    </Alert>
                  )}
                  {jobStatus?.worker && !jobStatus.worker.ready ? (
                    <Alert color="yellow" variant="light" title={t("Background worker not running")}>
                      <Stack spacing="xs">
                        <Text size="sm">
                          {t(
                            "Pending jobs stay at 0 attempts until the processFileBackupJobs worker runs. Check the items below."
                          )}
                        </Text>
                        <List size="sm" spacing={4}>
                          {!jobStatus.worker.enabled ? (
                            <List.Item>{t("Enable file backup sync on the Settings tab and click Save.")}</List.Item>
                          ) : null}
                          {!jobStatus.worker.credentialsConfigured ? (
                            <List.Item>{t("Configure the service account JSON and click Save.")}</List.Item>
                          ) : null}
                          {!jobStatus.worker.rootFolderConfigured ? (
                            <List.Item>{t("Set the Google Drive root folder ID and click Save.")}</List.Item>
                          ) : null}
                          {jobStatus.worker.enabled &&
                          jobStatus.worker.credentialsConfigured &&
                          jobStatus.worker.rootFolderConfigured &&
                          !jobStatus.worker.cronActive ? (
                            <List.Item>
                              {t(
                                "Turn on the processFileBackupJobs cron job under Management → Cron Jobs, or click Save on this page."
                              )}
                            </List.Item>
                          ) : null}
                          {jobStatus.worker.cronActive && !jobStatus.worker.cronScheduled ? (
                            <List.Item>
                              {t(
                                "The worker cron job is active in the database but not scheduled — restart the server or click Save on this page."
                              )}
                            </List.Item>
                          ) : null}
                        </List>
                        {jobStatus.worker.cronLastError ? (
                          <Text size="xs" color="red">
                            {t("Last worker error")}: {jobStatus.worker.cronLastError}
                          </Text>
                        ) : null}
                      </Stack>
                    </Alert>
                  ) : null}
                  {jobStatus?.worker?.ready && jobStatus.pending > 0 ? (
                    <Text size="xs" color="dimmed">
                      {t(
                        "Worker runs every 2 minutes (~{{rate}} jobs/hour). Use Process jobs now for an immediate batch.",
                        { rate: jobStatus.worker.estimatedJobsPerHour }
                      )}
                    </Text>
                  ) : null}
                  {jobStatus && (
                    <Group spacing="xl">
                      <Stack spacing={0}>
                        <Text size="xs" color="dimmed">{t("Pending jobs")}</Text>
                        <Text size="lg" weight={600}>{jobStatus.pending}</Text>
                      </Stack>
                      <Stack spacing={0}>
                        <Text size="xs" color="dimmed">{t("Completed jobs")}</Text>
                        <Text size="lg" weight={600}>{jobStatus.synced}</Text>
                      </Stack>
                      <Stack spacing={0}>
                        <Text size="xs" color="dimmed">{t("Failed jobs")}</Text>
                        <Text size="lg" weight={600} color={jobStatus.failed > 0 ? "red" : undefined}>
                          {jobStatus.failed}
                        </Text>
                      </Stack>
                      <Stack spacing={0}>
                        <Text size="xs" color="dimmed">{t("Files mirrored")}</Text>
                        <Text size="lg" weight={600}>{jobStatus.syncRecords}</Text>
                      </Stack>
                      {statusLoading && (
                        <Text size="xs" color="dimmed">{t("Refreshing…")}</Text>
                      )}
                    </Group>
                  )}
                </Stack>
              </FormSectionCard>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="syncJobs">
            <FileBackupSyncJobsTab
              api={api}
              onRefreshStatus={refreshStatus}
              jobStatus={jobStatus}
              statusLoading={statusLoading}
              isActive={activeTab === "syncJobs"}
            />
          </Tabs.Panel>

          <Tabs.Panel value="pathTemplates">
            <FormSectionCard>
              <Stack spacing="lg">
                <Stack spacing="xs">
                  <Title order={5}>{t("Path templates")}</Title>
                  <Text size="sm" color="dimmed">
                    {t(
                      "Choose which file types are backed up to Google Drive and how their folders are organized. Turn off types you do not need — for example, enable only Debit Note if the client only wants debit note documents mirrored."
                    )}
                  </Text>
                </Stack>
                {ENTITY_BACKUP_TYPE_META.map((meta) => {
                  const enabled =
                    formValues.entityTypeEnabled?.[meta.key] !== false;
                  return (
                    <Stack
                      key={meta.key}
                      spacing="sm"
                      p="md"
                      style={{
                        border: "1px solid var(--mantine-color-dark-4)",
                        borderRadius: 8,
                      }}
                    >
                      <Switch
                        label={t("Backup this file type")}
                        checked={enabled}
                        onChange={(event) =>
                          updateField(
                            `entityTypeEnabled.${meta.key}`,
                            event.currentTarget.checked
                          )
                        }
                      />
                      <Title order={6}>{t(meta.label)}</Title>
                      <Text size="sm" color="dimmed">
                        {t(meta.description)}
                      </Text>
                      <TextInput
                        label={t("Folder path template")}
                        description={t(
                          "Placeholders: {year}, {yearMonth}, {month}, {day}, {code}, {entityId}, {uploadFolder}"
                        )}
                        value={formValues.pathTemplates?.[meta.key] || ""}
                        disabled={!enabled}
                        onChange={(event) =>
                          updateField(
                            `pathTemplates.${meta.key}`,
                            event.currentTarget.value
                          )
                        }
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </FormSectionCard>
          </Tabs.Panel>

          <Tabs.Panel value="advanced">
            <FormSectionCard>
              <Stack spacing="sm">
                <Title order={5}>{t("Advanced settings")}</Title>
                <Text size="sm" color="dimmed">
                  {t(
                    "Control how the background worker processes backup jobs. Increase batch size for faster catch-up; increase max retries for flaky network conditions."
                  )}
                </Text>
                <NumberInput
                  label={t("Max retries")}
                  min={1}
                  value={formValues.worker?.maxRetries ?? DEFAULT_WORKER.maxRetries}
                  onChange={(value) => updateField("worker.maxRetries", value)}
                />
                <NumberInput
                  label={t("Batch size")}
                  min={1}
                  value={formValues.worker?.batchSize ?? DEFAULT_WORKER.batchSize}
                  onChange={(value) => updateField("worker.batchSize", value)}
                />
                <NumberInput
                  label={t("Upload concurrency")}
                  description={t(
                    "How many backup jobs run in parallel within each worker batch."
                  )}
                  min={1}
                  max={10}
                  value={formValues.worker?.concurrency ?? DEFAULT_WORKER.concurrency}
                  onChange={(value) => updateField("worker.concurrency", value)}
                />
              </Stack>
            </FormSectionCard>
          </Tabs.Panel>

          <Tabs.Panel value="setupGuide">
            <FileBackupSetupGuideTab
              serviceAccountEmail={displayedServiceAccountEmail}
            />
          </Tabs.Panel>
        </Tabs>
      )}

      <FileBackupErrorHelpModal
        opened={connectionHelpOpen}
        onClose={() => setConnectionHelpOpen(false)}
        presentation={connectionHelp}
      />
    </Page>
  );
};

export default FileBackupSyncSetting;
